// Live Events WebSocket Service
import { WebPubSubClient } from '@azure/web-pubsub-client'

export interface LiveEvent {
  id: string
  type: 'session_start' | 'session_end' | 'screen_view' | 'custom_event' | 'crash'
  appId: string
  appName: string
  timestamp: string
  country?: string
  city?: string
  latitude?: number
  longitude?: number
  platform?: 'ios' | 'macos' | 'visionos' | 'tvos' | 'watchos' | 'android'
  osVersion?: string
  deviceModel?: string
  screenResolution?: string
  locale?: string
  timezone?: string
  appVersion?: string
  buildNumber?: string
  sdkVersion?: string
  sdkPlatform?: string
  eventName?: string
  screenName?: string
  crashReason?: string
  sessionId?: string
  userId?: string
}

interface EventStreamMessage {
  messageType: 'event' | 'stats' | 'heartbeat'
  data: any
  timestamp: string
}

type EventCallback = (event: LiveEvent) => void
type StatsCallback = (stats: any) => void
type ConnectedCallback = () => void
type DisconnectedCallback = () => void
type TokenExpiredCallback = () => void

/**
 * Verbose per-message logging. Off by default: the portfolio map keeps a socket
 * open for hours, and three console lines per event is enough retained garbage
 * to visibly degrade a long-lived tab. Enable with
 * `localStorage.setItem('as_debug_live', '1')` when diagnosing the stream.
 */
function isDebugLoggingEnabled(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('as_debug_live') === '1'
  } catch {
    return false
  }
}

const MAX_RECONNECT_DELAY_MS = 30_000

export class LiveEventsService {
  private client: WebPubSubClient | null = null
  private isConnected = false
  private isConnecting = false
  private eventCallbacks: EventCallback[] = []
  private statsCallbacks: StatsCallback[] = []
  private connectedCallbacks: ConnectedCallback[] = []
  private disconnectedCallbacks: DisconnectedCallback[] = []
  private tokenExpiredCallbacks: TokenExpiredCallback[] = []
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 2000 // 2 seconds
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private lastUserId: string | null = null
  private lastAccountId: string | null = null
  private lastAppId: string | null = null
  private tokenExpiredNotified = false
  /**
   * Incremented every time a client is created. Handlers close over the epoch
   * they were registered under and bail out if a newer client has since taken
   * over, so a socket that is still winding down can never deliver events,
   * flip connection state, or schedule a reconnect on behalf of the live one.
   */
  private clientEpoch = 0

  /**
   * Connect to Web PubSub
   */
  async connect(userId: string, accountId?: string, appId?: string): Promise<void> {
    if (this.isConnected) {
      return
    }

    if (this.isConnecting) {
      return
    }

    this.isConnecting = true

    // A caller-driven connect supersedes any pending automatic retry.
    this.clearReconnectTimer()

    // Store connection parameters for reconnection
    this.lastUserId = userId
    this.lastAccountId = accountId || null
    this.lastAppId = appId || null

    // Drop any previous client before building a new one. Without this the old
    // WebSocket stays open with its handlers attached, so every reconnect used
    // to add another live socket delivering the same events — the event rate
    // (and the render load it drives) doubled on each drop.
    this.teardownClient()

    const epoch = ++this.clientEpoch
    const isCurrent = () => epoch === this.clientEpoch

    try {
      // Get connection URL from negotiate endpoint
      const apiUrl = import.meta.env.VITE_REALTIME_API_URL || 'http://localhost:3002'
      const params = new URLSearchParams({
        ...(appId && { appId }),
      })

      // Get JWT token from localStorage
      const token = localStorage.getItem('as_token')
      if (!token) {
        this.isConnecting = false
        throw new Error('No authentication token found')
      }

      const response = await fetch(`${apiUrl}/negotiate?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      // Handle token expiration
      if (response.status === 401) {
        this.isConnecting = false
        // Only notify once to prevent infinite loops
        if (!this.tokenExpiredNotified) {
          console.warn('⚠️ Token expired or invalid - user needs to log in again')
          this.tokenExpiredNotified = true
          this.tokenExpiredCallbacks.forEach(cb => cb())
        }
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`Token expired: ${errorData.error || 'Please log in again'}`)
      }

      if (!response.ok) {
        this.isConnecting = false
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`Failed to negotiate connection: ${response.status} - ${errorData.error || response.statusText}`)
      }

      const { url } = await response.json()

      // A newer connect() may have started while we were negotiating.
      if (!isCurrent()) {
        this.isConnecting = false
        return
      }

      // Create WebPubSub client
      const client = new WebPubSubClient(url)
      this.client = client

      // Set up event handlers
      client.on('connected', () => {
        if (!isCurrent()) return
        this.isConnected = true
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.tokenExpiredNotified = false // Reset flag on successful connection
        console.log('✓ Connected to live events')
        this.connectedCallbacks.forEach(cb => cb())
      })

      client.on('disconnected', (e) => {
        if (!isCurrent()) return
        this.isConnected = false
        this.isConnecting = false
        console.log('✗ Disconnected from live events:', e.message)
        this.disconnectedCallbacks.forEach(cb => cb())

        // Auto-reconnect with capped exponential backoff. Only one retry may be
        // in flight at a time.
        if (this.reconnectAttempts < this.maxReconnectAttempts && !this.reconnectTimer) {
          this.reconnectAttempts++
          const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, MAX_RECONNECT_DELAY_MS)
          console.log(`Reconnecting... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
          this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null
            if (!isCurrent()) return
            this.connect(
              this.lastUserId ?? userId,
              this.lastAccountId ?? accountId,
              this.lastAppId ?? appId
            ).catch(error => console.error('Live events reconnect failed:', error))
          }, delay)
        }
      })

      client.on('server-message', (e) => {
        if (!isCurrent()) return
        const debug = isDebugLoggingEnabled()
        try {
          const rawData = e.message.data
          const message: EventStreamMessage = typeof rawData === 'string'
            ? JSON.parse(rawData)
            : rawData as EventStreamMessage

          if (message.messageType === 'event') {
            const event = message.data as LiveEvent
            if (debug) console.log('[LiveEvents] Event received:', event.id, event.type, event.appName)
            this.eventCallbacks.forEach(cb => cb(event))
          } else if (message.messageType === 'stats') {
            if (debug) console.log('[LiveEvents] Stats update received')
            this.statsCallbacks.forEach(cb => cb(message.data))
          }
        } catch (error) {
          console.error('Failed to parse message:', error, 'raw data:', e.message.data)
        }
      })

      // Start the connection
      await client.start()

      // Another connect() won the race while start() was pending — retire this one.
      if (!isCurrent()) {
        try {
          client.stop()
        } catch {
          // Already stopping; nothing to clean up.
        }
      }
    } catch (error) {
      this.isConnecting = false
      console.error('Failed to connect to live events:', error)
      throw error
    }
  }

  /**
   * Stop and release the current client without touching connection parameters
   * or subscriber callbacks. Bumping the epoch orphans its handlers first so a
   * late 'disconnected' from the retiring socket cannot schedule a reconnect.
   */
  private teardownClient(): void {
    const client = this.client
    if (!client) return
    this.clientEpoch++
    this.client = null
    this.isConnected = false
    try {
      client.stop()
    } catch (error) {
      console.error('Error releasing previous live events client:', error)
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  /**
   * Disconnect from Web PubSub
   */
  async disconnect(): Promise<void> {
    this.clearReconnectTimer()

    if (!this.client) {
      this.isConnecting = false
      return
    }

    const client = this.client
    this.clientEpoch++
    this.client = null
    this.isConnected = false
    this.isConnecting = false

    try {
      await client.stop()
      console.log('✓ Disconnected from live events')
    } catch (error) {
      console.error('Error disconnecting:', error)
    }
  }

  /**
   * Subscribe to live events
   */
  onEvent(callback: EventCallback): () => void {
    this.eventCallbacks.push(callback)

    // Return unsubscribe function
    return () => {
      this.eventCallbacks = this.eventCallbacks.filter(cb => cb !== callback)
    }
  }

  /**
   * Subscribe to stats updates
   */
  onStats(callback: StatsCallback): () => void {
    this.statsCallbacks.push(callback)

    return () => {
      this.statsCallbacks = this.statsCallbacks.filter(cb => cb !== callback)
    }
  }

  /**
   * Subscribe to connection state changes
   */
  onConnected(callback: ConnectedCallback): () => void {
    this.connectedCallbacks.push(callback)

    return () => {
      this.connectedCallbacks = this.connectedCallbacks.filter(cb => cb !== callback)
    }
  }

  onDisconnected(callback: DisconnectedCallback): () => void {
    this.disconnectedCallbacks.push(callback)

    return () => {
      this.disconnectedCallbacks = this.disconnectedCallbacks.filter(cb => cb !== callback)
    }
  }

  /**
   * Subscribe to token expiration events
   */
  onTokenExpired(callback: TokenExpiredCallback): () => void {
    this.tokenExpiredCallbacks.push(callback)

    return () => {
      this.tokenExpiredCallbacks = this.tokenExpiredCallbacks.filter(cb => cb !== callback)
    }
  }

  /**
   * Reconnect with fresh token (call after user re-authenticates)
   */
  async reconnectWithNewToken(): Promise<void> {
    if (!this.lastUserId) {
      throw new Error('No previous connection to reconnect')
    }

    // Disconnect first
    await this.disconnect()

    // Reset reconnect attempts and token expired flag
    this.reconnectAttempts = 0
    this.tokenExpiredNotified = false

    // Reconnect with stored parameters
    await this.connect(this.lastUserId, this.lastAccountId || undefined, this.lastAppId || undefined)
  }

  /**
   * Fetch recent events from the server to initialize the map/charts
   */
  async getRecentEvents(appId?: string): Promise<LiveEvent[]> {
    try {
      const apiUrl = import.meta.env.VITE_REALTIME_API_URL || 'http://localhost:3002'
      const params = new URLSearchParams()
      if (appId) params.append('appId', appId)

      const token = localStorage.getItem('as_token')
      const headers: Record<string, string> = {}
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch(`${apiUrl}/recent-events?${params}`, { headers })
      if (!response.ok) throw new Error('Failed to fetch recent events')

      const data = await response.json()
      return data.events || []
    } catch (error) {
      console.error('[LiveEventsService] Failed to fetch recent events:', error)
      return []
    }
  }

  /**
   * Check if connected
   */
  getIsConnected(): boolean {
    return this.isConnected
  }
}

// Persist the singleton on `window` so Vite HMR module reloads reuse the
// existing instance rather than creating a new one (which would leave the old
// WebSocket connection dangling and cause duplicate message delivery).
const HMR_KEY = '__appstats_liveEventsService__' as const

function getOrCreateService(): LiveEventsService {
  if (typeof window === 'undefined') return new LiveEventsService()
  const w = window as any
  if (!w[HMR_KEY]) w[HMR_KEY] = new LiveEventsService()
  return w[HMR_KEY]
}

export const liveEventsService = getOrCreateService()
